import React, { useState, useEffect } from 'react';
import { applicationService } from '../../services/applicationService';
import { jobService } from '../../services/jobService';

export default function ApplyModal({ isOpen, onClose, defaultJobKey, source = 'modal' }) {
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position_key: defaultJobKey || '',
    note: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ text: '', type: '' });
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    jobService
      .getPublicJobs()
      .then((res) => {
        if (res.data) setJobs(res.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultJobKey) {
      setFormData((prev) => ({ ...prev, position_key: defaultJobKey }));
    }
  }, [defaultJobKey]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('apply-modal-open');
    } else {
      document.body.classList.remove('apply-modal-open');
    }
    return () => document.body.classList.remove('apply-modal-open');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      if (selected.size > 10 * 1024 * 1024) {
        setStatusMsg({ text: 'File CV vượt quá 10MB. Vui lòng chọn file nhỏ hơn.', type: 'error' });
        return;
      }
      setFile(selected);
      setStatusMsg({ text: '', type: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { full_name, email, phone, position_key, note } = formData;

    if (!full_name || !email || !phone || !file) {
      setStatusMsg({ text: 'Vui lòng điền đầy đủ thông tin và đính kèm file CV.', type: 'error' });
      return;
    }

    const selectedJob = jobs.find((j) => j.key === position_key);
    const position_label = selectedJob ? selectedJob.title : position_key === '__other__' ? 'Vị trí khác' : position_key;

    const data = new FormData();
    data.append('full_name', full_name);
    data.append('email', email);
    data.append('phone', phone);
    data.append('position_key', position_key === '__other__' ? '' : position_key);
    data.append('position_label', position_label);
    data.append('note', note);
    data.append('source', source);
    data.append('cv', file);

    setLoading(true);
    setStatusMsg({ text: '', type: '' });

    try {
      const res = await applicationService.submitApplication(data);
      setSuccessData({
        ...res.data,
        email,
        phone,
        position_label
      });
    } catch (err) {
      setStatusMsg({ text: err.message || 'Lỗi nộp hồ sơ. Vui lòng thử lại.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSuccessData(null);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      position_key: '',
      note: ''
    });
    setFile(null);
    setStatusMsg({ text: '', type: '' });
    onClose();
  };

  return (
    <div className="apply-modal" role="dialog" aria-modal="true">
      <div className="apply-modal__backdrop" onClick={handleReset} />
      <div className="apply-modal__panel" role="document">
        <button type="button" className="apply-modal__close" onClick={handleReset} aria-label="Đóng">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {!successData ? (
          <div className="apply-modal__body">
            <span className="eyebrow">Ứng tuyển trực tuyến</span>
            <h2>Gửi hồ sơ gia nhập Thế Thảo</h2>
            <p className="lead">
              Đội ngũ Nhân sự sẽ liên hệ phản hồi trong vòng <strong>24 giờ làm việc</strong>.
            </p>

            <form onSubmit={handleSubmit} className="apply-form" style={{ marginTop: '20px' }}>
              <div className="apply-form__field">
                <label className="apply-form__label" htmlFor="applyName">Họ và tên <em>*</em></label>
                <input
                  id="applyName"
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Nguyễn Văn A"
                  required
                />
              </div>

              <div className="apply-form__row">
                <div className="apply-form__field">
                  <label className="apply-form__label" htmlFor="applyEmail">Email <em>*</em></label>
                  <input
                    id="applyEmail"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@gmail.com"
                    required
                  />
                </div>
                <div className="apply-form__field">
                  <label className="apply-form__label" htmlFor="applyPhone">Số điện thoại <em>*</em></label>
                  <input
                    id="applyPhone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="0989 xxx xxx"
                    required
                  />
                </div>
              </div>

              <div className="apply-form__field">
                <label className="apply-form__label" htmlFor="applyPosition">Vị trí ứng tuyển <em>*</em></label>
                <select
                  id="applyPosition"
                  name="position_key"
                  value={formData.position_key}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Chọn vị trí mong muốn --</option>
                  {jobs.map((j) => (
                    <option key={j.key} value={j.key}>
                      {j.title} {j.headcount > 0 ? `· Tuyển ${j.headcount}` : ''}
                    </option>
                  ))}
                  <option value="__other__">Vị trí khác</option>
                </select>
              </div>

              <div className="apply-form__field">
                <label className="apply-form__label" htmlFor="applyCv">Đính kèm CV của bạn <em>*</em></label>
                <input
                  id="applyCv"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  required
                />
                <span className="apply-form__hint">PDF / DOC / DOCX, tối đa 10MB.</span>
              </div>

              <div className="apply-form__field">
                <label className="apply-form__label" htmlFor="applyNote">Ghi chú / Giới thiệu bản thân</label>
                <textarea
                  id="applyNote"
                  name="note"
                  rows="3"
                  value={formData.note}
                  onChange={handleChange}
                  placeholder="Kinh nghiệm, thời gian có thể bắt đầu đi làm..."
                />
              </div>

              <div className="apply-form__actions">
                <button
                  type="submit"
                  className="btn btn--primary apply-form__submit"
                  disabled={loading}
                >
                  {loading ? 'Đang tải lên hồ sơ…' : 'Gửi hồ sơ ứng tuyển'}
                </button>
                {statusMsg.text && (
                  <p className={`apply-form__note ${statusMsg.type ? 'is-' + statusMsg.type : ''}`}>
                    {statusMsg.text}
                  </p>
                )}
              </div>
            </form>
          </div>
        ) : (
          <div className="apply-success">
            <div className="apply-success__icon" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2>Nộp hồ sơ thành công!</h2>
            <p className="apply-modal__lead">
              Cảm ơn bạn <strong>{successData.full_name}</strong> đã quan tâm và gửi hồ sơ vị trí <strong>{successData.position_label}</strong>. Bộ phận Nhân sự sẽ liên hệ qua email <strong>{successData.email}</strong> hoặc số điện thoại <strong>{successData.phone}</strong> trong thời gian sớm nhất.
            </p>
            <dl className="apply-success__meta">
              <div><dt>Vị trí ứng tuyển</dt><dd>{successData.position_label}</dd></div>
              <div><dt>Mã hồ sơ</dt><dd className="apply-success__ref">{successData.ref}</dd></div>
            </dl>
            <p className="apply-success__hint">Bạn có thể chụp lại mã hồ sơ phòng khi cần đối chiếu với HR.</p>
            <button
              type="button"
              className="btn btn--primary apply-success__close"
              onClick={handleReset}
            >
              Hoàn tất
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
